import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  BarChart3,
  Users,
  Settings,
  Building2,
  TreePine,
  Eye,
  UserPlus,
  Trash2,
  Pencil,
  ArrowLeft,
  Mail,
  Home,
  ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [role, setRole] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [employees, setEmployees] = useState([]);

  const fetchCompany = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/corporate/me`, { withCredentials: true });
      setCompany(r.data.company);
      setRole(r.data.role);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      else if (err.response?.status === 404) {
        toast.error('You are not part of any company');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/corporate/me/analytics`, { withCredentials: true });
      setAnalytics(r.data);
    } catch (_err) {}
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/corporate/me/employees`, { withCredentials: true });
      setEmployees(r.data);
    } catch (_err) {}
  }, []);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  useEffect(() => {
    if (role !== 'admin') return;
    if (tab === 'stats') fetchAnalytics();
    if (tab === 'employees') fetchEmployees();
  }, [tab, role, fetchAnalytics, fetchEmployees]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F1EA] p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#B8935A]/20 flex items-center justify-center mb-6">
            <Building2 className="w-8 h-8 text-[#B8935A]" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-[#0A0A0A] mb-3">
            You're a member of {company?.name}
          </h1>
          <p className="text-[#525252] mb-6 text-sm">
            Only the company admin can access this dashboard. Head to your personal dashboard to edit
            your card.
          </p>
          <Link to="/dashboard">
            <Button className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-11 px-6">
              Go to my dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="corporate-dashboard">
      {/* Top bar */}
      <div className="border-b border-[#0A0A0A]/10 bg-[#F5F1EA]/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="text-[#525252] hover:text-[#0A0A0A]" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-9 h-9 rounded-lg bg-[#B8935A]/20 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-[#B8935A]" />
            </div>
            <div className="min-w-0">
              <p className="text-[#0A0A0A] font-semibold truncate" data-testid="company-name-header">{company.name}</p>
              <p className="text-xs text-[#525252] truncate">@{company.domain} · Corporate Admin</p>
            </div>
          </div>
          <Link to="/" className="hidden sm:flex items-center gap-1 text-sm text-[#525252] hover:text-[#0A0A0A]">
            <Home className="w-4 h-4" /> Home
          </Link>
        </div>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 flex gap-1 -mb-px overflow-x-auto">
          {[
            { id: 'stats', label: 'Dashboard', icon: BarChart3 },
            { id: 'employees', label: 'Employees', icon: Users },
            { id: 'settings', label: 'Settings', icon: Settings },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors text-sm whitespace-nowrap ${
                tab === t.id
                  ? 'border-[#B8935A] text-[#B8935A]'
                  : 'border-transparent text-[#525252] hover:text-[#0A0A0A]'
              }`}
              data-testid={`corp-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        {tab === 'stats' && <StatsTab analytics={analytics} />}
        {tab === 'employees' && (
          <EmployeesTab
            employees={employees}
            domain={company.domain}
            onChange={fetchEmployees}
          />
        )}
        {tab === 'settings' && <SettingsTab company={company} onUpdate={fetchCompany} />}
      </main>
    </div>
  );
};

// -------------------- Stats tab --------------------
const StatsTab = ({ analytics }) => {
  if (!analytics) {
    return <div className="text-[#525252]">Loading analytics…</div>;
  }
  return (
    <div className="space-y-6" data-testid="stats-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total views" value={analytics.totals.views} icon={Eye} testid="stat-views" />
        <StatCard label="Total leads" value={analytics.totals.leads} icon={Mail} testid="stat-leads" />
        <StatCard label="Active profiles" value={analytics.totals.active_profiles} icon={Users} testid="stat-profiles" />
        <StatCard label="Trees saved" value={analytics.totals.trees_saved} icon={TreePine} testid="stat-trees" />
      </div>

      <div>
        <h2 className="text-[#0A0A0A] font-semibold mb-3">Top performers</h2>
        <div className="bg-white rounded-2xl overflow-hidden border border-[#0A0A0A]/10">
          {analytics.top_performers.length === 0 ? (
            <p className="p-6 text-[#525252] text-sm">No employees yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#525252] text-left">
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">Views</th>
                  <th className="px-4 py-3 font-medium">Leads</th>
                  <th className="px-4 py-3 font-medium">Profile</th>
                </tr>
              </thead>
              <tbody>
                {analytics.top_performers.map((e) => (
                  <tr key={e.user_id} className="border-t border-[#0A0A0A]/10">
                    <td className="px-4 py-3 text-[#0A0A0A]">
                      <div className="font-medium">{e.name}</div>
                      <div className="text-xs text-[#525252]">{e.email}</div>
                    </td>
                    <td className="px-4 py-3 text-[#0A0A0A]">{e.views}</td>
                    <td className="px-4 py-3 text-[#0A0A0A]">{e.leads}</td>
                    <td className="px-4 py-3">
                      {e.username ? (
                        <a
                          href={`/p/${e.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#B8935A] hover:text-[#B8935A] text-xs flex items-center gap-1"
                        >
                          /p/{e.username} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-[#a3a3a3] text-xs">No profile</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div>
        <h2 className="text-[#0A0A0A] font-semibold mb-3">All employees ({analytics.per_employee.length})</h2>
        <div className="bg-white rounded-2xl overflow-hidden border border-[#0A0A0A]/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[#525252] text-left">
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Views</th>
                <th className="px-4 py-3 font-medium">Leads</th>
                <th className="px-4 py-3 font-medium">Last active</th>
              </tr>
            </thead>
            <tbody>
              {analytics.per_employee.map((e) => (
                <tr key={e.user_id} className="border-t border-[#0A0A0A]/10">
                  <td className="px-4 py-3 text-[#0A0A0A]">
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-[#525252]">{e.email}</div>
                  </td>
                  <td className="px-4 py-3 text-[#0A0A0A] capitalize">{e.role}</td>
                  <td className="px-4 py-3 text-[#0A0A0A]">{e.views}</td>
                  <td className="px-4 py-3 text-[#0A0A0A]">{e.leads}</td>
                  <td className="px-4 py-3 text-[#525252] text-xs">
                    {e.last_active ? new Date(e.last_active).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white rounded-2xl p-4 border border-[#0A0A0A]/10" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs text-[#525252] uppercase tracking-wider">{label}</p>
      <Icon className="w-4 h-4 text-[#B8935A]" />
    </div>
    <p className="text-2xl font-bold text-[#0A0A0A]">{value}</p>
  </div>
);

// -------------------- Employees tab --------------------
const EmployeesTab = ({ employees, domain, onChange }) => {
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [overrideId, setOverrideId] = useState(null);
  const [overrideData, setOverrideData] = useState({ job_title: '', company: '', company_logo: '' });

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addEmail) return;
    setAdding(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/corporate/me/employees`,
        { email: addEmail },
        { withCredentials: true }
      );
      toast.success('Employee added');
      setAddEmail('');
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove this employee from your company?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/corporate/me/employees/${userId}`, { withCredentials: true });
      toast.success('Employee removed');
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleOverride = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/corporate/me/employees/${overrideId}/override`,
        overrideData,
        { withCredentials: true }
      );
      toast.success('Profile updated');
      setOverrideId(null);
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-tab">
      <div className="bg-white rounded-2xl p-6 border border-[#0A0A0A]/10">
        <h2 className="text-[#0A0A0A] font-semibold mb-1">Add employee</h2>
        <p className="text-[#525252] text-sm mb-4">
          The user must already be registered at <span className="text-[#B8935A]">/corporate</span> with
          an <span className="text-[#0A0A0A]">@{domain}</span> email.
        </p>
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            placeholder={`employee@${domain}`}
            className="flex-1 bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
            data-testid="add-employee-email"
          />
          <Button
            type="submit"
            disabled={adding}
            className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-11 px-6"
            data-testid="add-employee-submit"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {adding ? 'Adding…' : 'Add employee'}
          </Button>
        </form>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden border border-[#0A0A0A]/10">
        <div className="px-6 py-4 border-b border-[#0A0A0A]/10">
          <h2 className="text-[#0A0A0A] font-semibold">Employees ({employees.length})</h2>
        </div>
        {employees.length === 0 ? (
          <p className="p-6 text-[#525252] text-sm">No employees yet.</p>
        ) : (
          <div className="divide-y divide-[#0A0A0A]/10">
            {employees.map((e) => (
              <div key={e.user_id} className="p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3" data-testid={`employee-row-${e.user_id}`}>
                <div className="flex items-center gap-3">
                  {e.profile_picture ? (
                    <img src={e.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#0A0A0A] font-bold">
                      {e.name?.[0] || '?'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[#0A0A0A] font-medium truncate">{e.name}</p>
                    <p className="text-xs text-[#525252] truncate">{e.email}</p>
                    {e.profile_job_title && (
                      <p className="text-xs text-[#a3a3a3] truncate">{e.profile_job_title}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-[#525252]">
                    {e.views} views · {e.leads} leads
                  </span>
                  {e.role === 'admin' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#B8935A]/20 text-[#B8935A]">
                      Admin
                    </span>
                  )}
                  {e.profile_username ? (
                    <a
                      href={`/p/${e.profile_username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#B8935A] hover:text-[#B8935A]"
                    >
                      View card
                    </a>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setOverrideId(e.user_id);
                      setOverrideData({ job_title: '', company: '', company_logo: '' });
                    }}
                    className="border-[#0A0A0A]/15 text-[#525252] hover:bg-[#EBE6DC] h-8 px-3"
                    data-testid={`override-${e.user_id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Override
                  </Button>
                  {e.role !== 'admin' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemove(e.user_id)}
                      className="border-red-700 text-red-400 hover:bg-red-500/10 h-8 px-3"
                      data-testid={`remove-${e.user_id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Override modal */}
      {overrideId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOverrideId(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-[#0A0A0A]/10" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
            <h3 className="text-xl font-bold text-[#0A0A0A] mb-1">Override employee fields</h3>
            <p className="text-xs text-[#525252] mb-5">
              Leave blank to keep current value. These overwrite the employee's own settings.
            </p>
            <div className="space-y-4">
              <div>
                <Label className="text-[#0A0A0A] mb-2 block">Job title</Label>
                <Input
                  value={overrideData.job_title}
                  onChange={(e) => setOverrideData({ ...overrideData, job_title: e.target.value })}
                  placeholder="e.g. Senior Engineer"
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
                  data-testid="override-job-title"
                />
              </div>
              <div>
                <Label className="text-[#0A0A0A] mb-2 block">Company name</Label>
                <Input
                  value={overrideData.company}
                  onChange={(e) => setOverrideData({ ...overrideData, company: e.target.value })}
                  placeholder="e.g. AcmeCo"
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
                  data-testid="override-company"
                />
              </div>
              <div>
                <Label className="text-[#0A0A0A] mb-2 block">Company logo URL</Label>
                <Input
                  value={overrideData.company_logo}
                  onChange={(e) => setOverrideData({ ...overrideData, company_logo: e.target.value })}
                  placeholder="https://..."
                  className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
                  data-testid="override-logo"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={() => setOverrideId(null)} variant="outline" className="flex-1 border-[#0A0A0A]/15 text-[#525252] hover:bg-[#EBE6DC] rounded-full">
                Cancel
              </Button>
              <Button onClick={handleOverride} className="flex-1 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full" data-testid="override-save">
                Save overrides
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------- Settings tab --------------------
const SettingsTab = ({ company, onUpdate }) => {
  const [form, setForm] = useState({
    name: company.name || '',
    description: company.description || '',
    logo: company.logo || '',
    allow_self_signup: company.allow_self_signup,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/corporate/me`, form, { withCredentials: true });
      toast.success('Company info updated');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-tab">
      <form onSubmit={handleSave} className="bg-white rounded-2xl p-6 border border-[#0A0A0A]/10 space-y-4">
        <h2 className="text-[#0A0A0A] font-semibold mb-2">Company info</h2>
        <div>
          <Label className="text-[#0A0A0A] mb-2 block">Company name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
            data-testid="settings-name"
          />
        </div>
        <div>
          <Label className="text-[#0A0A0A] mb-2 block">Description</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What your company does"
            className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl min-h-[100px]"
            data-testid="settings-description"
          />
        </div>
        <div>
          <Label className="text-[#0A0A0A] mb-2 block">Logo URL</Label>
          <Input
            value={form.logo}
            onChange={(e) => setForm({ ...form, logo: e.target.value })}
            placeholder="https://..."
            className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
            data-testid="settings-logo"
          />
        </div>
        <div className="flex items-center justify-between bg-[#F5F1EA] rounded-xl p-4 border border-[#0A0A0A]/10">
          <div>
            <p className="text-[#0A0A0A] font-medium text-sm">Allow self-signup</p>
            <p className="text-xs text-[#525252]">Anyone with @{company.domain} can sign up</p>
          </div>
          <button
            type="button"
            onClick={() => setForm({ ...form, allow_self_signup: !form.allow_self_signup })}
            className={`relative w-12 h-6 rounded-full transition-colors ${form.allow_self_signup ? 'bg-[#0A0A0A]' : 'bg-white'}`}
            data-testid="settings-self-signup-toggle"
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.allow_self_signup ? 'left-6' : 'left-0.5'}`} />
          </button>
        </div>
        <Button type="submit" disabled={saving} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-11 px-6" data-testid="settings-save">
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </form>

      <div className="bg-white rounded-2xl p-6 border border-[#0A0A0A]/10">
        <h2 className="text-[#0A0A0A] font-semibold mb-3">Subscription</h2>
        <div className="flex items-center justify-between bg-[#F5F1EA] rounded-xl p-4 border border-[#0A0A0A]/10">
          <div>
            <p className="text-[#0A0A0A] font-medium text-sm capitalize" data-testid="sub-status">
              {company.subscription_status || 'inactive'}
            </p>
            <p className="text-xs text-[#525252]">
              {company.num_cards_paid > 0
                ? `${company.num_cards_paid} cards paid`
                : 'Contact support@soldigital.in to activate'}
            </p>
          </div>
          {company.subscription_status !== 'active' && (
            <a href="mailto:support@soldigital.in?subject=Activate%20Corporate%20Subscription">
              <Button variant="outline" className="border-[#B8935A] text-[#B8935A] hover:bg-[#B8935A]/10 rounded-full h-10">
                Contact sales
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;
